/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Const } from "const";
import { FC, memo, useCallback } from "react";
import { convertLatLngFromGeolocation } from "utils/Map";
import { ChargerItem } from "hooks/GetChargersHook";
import { MarkerF } from "@react-google-maps/api";
import { useTaskRegisterDataContext } from "context/TaskRegisterDataProvider";
import batteryChargeIcon from "assets/battery_charge.svg";
import { useMapDataContext } from "context/MapDataProvider";
import cssClass from "./NodeMapOverlay.module.css";
import { getNodeType } from "./NodeMapOverlay";

interface Props {
  chargers?: ChargerItem[];
  selected?: string[];
}
export const ChargerMapOverlay: FC<Props> = memo((props: Props) => {
  const { chargers } = useTaskRegisterDataContext();
  const targetChargers = props.chargers ?? chargers;
  const { mapData } = useMapDataContext();

  const iconOnNode: google.maps.Icon = {
    url: batteryChargeIcon,
    anchor: new google.maps.Point(22, 25),
    scaledSize: new google.maps.Size(50, 50),
    labelOrigin: new google.maps.Point(22, 50),
  };

  const iconOnNamedNode: google.maps.Icon = {
    ...iconOnNode,
    labelOrigin: new google.maps.Point(22, 75),
  };

  const iconHighlight: google.maps.Symbol = {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: "#00B2FF",
    fillOpacity: 0.5,
    strokeWeight: 0,
    scale: 40,
  };

  const isSelected = useCallback(
    (chargerId: string) => {
      return (
        props.selected?.find((selectedId) => selectedId === chargerId) !==
        undefined
      );
    },
    [props.selected]
  );

  const nodes = mapData?.mapInfo.flatMap((floor) => floor.nodes);
  const markersProps = targetChargers?.map((charger) => {
    const settingNode = nodes.find(
      (node) => node?.id === charger.settingNodeId
    );
    return {
      id: charger.id,
      position: convertLatLngFromGeolocation(settingNode?.geolocation)!,
      icon:
        getNodeType(settingNode?.description).type === "NamedNode"
          ? iconOnNamedNode
          : iconOnNode,
      label: charger.nameId.length
        ? {
            color: "white",
            className: cssClass.markerLabelOutline,
            text: charger.nameId,
          }
        : undefined,
      zIndex: Const.zIndex.namedNode + 1,
    };
  });
  return (
    <>
      {markersProps?.map(({ id, position, ...props }) => (
        <>
          {position && (
            <>
              <MarkerF {...props} key={`charger-${id}`} position={position} />
              {isSelected(id) && (
                <MarkerF
                  key={`charger-highlight-${id}`}
                  icon={iconHighlight}
                  position={position}
                  zIndex={Const.zIndex.namedNode + 2}
                />
              )}
            </>
          )}
        </>
      ))}
    </>
  );
});
